import { Environment, getEnvironment } from '../../../common/src/environment.ts';
import { SocketMessage } from '../../../common/src/model.ts';
import { isHeartbeatMessage } from '../../../common/src/model_util.ts';
import { liveUpdateHeartbeatMs, liveUpdatesSocketPath } from '../../../common/src/server_api.ts';
import { subscribePageForeground } from './page_foreground.ts';

// The client half of the live-update transport (GitHub issue #3265). Sockets are an accelerator
// only - every update this connection announces is also noticed by the app's ordinary pull checks -
// so this module's whole job is to keep a socket open when there is a user to serve, to give up
// quietly when there is not, and to tell its owner every time a socket opens so the owner can catch
// up on whatever happened while it was closed. It touches no React and registers no global
// listeners: the browser events that drive it arrive as the note*() methods, so the state machine is
// testable under fake timers.

export const liveUpdateBackoffInitialMs = 1_000;
export const liveUpdateBackoffMaxMs = 30_000;
// a minute of silence is two missed heartbeats plus slack - the socket is dead even if the browser
// has not said so. Derived from the server's interval so the two can never drift apart.
export const liveUpdateWatchdogMs = 2 * liveUpdateHeartbeatMs + 10_000;
// a handshake that neither completes nor fails (a proxy that accepts the TCP connection and never
// answers the upgrade, a path black-holed while the phone was asleep) would otherwise pin the
// connection in 'connecting' for the life of the tab
export const liveUpdateConnectTimeoutMs = 15_000;
// an open socket should mean an active user, so a tab left hidden this long gives its socket up
export const liveUpdateHiddenCloseMs = 10 * 60_000;

// the subset of the browser WebSocket this module uses, so tests can substitute a fake
export interface LiveUpdateSocket {
  readonly readyState: number;
  onopen: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  close(code?: number, reason?: string): void;
}

export interface LiveUpdateConnectInfo {
  // false for the first open after start(); true for every open after a disconnect, which is when
  // something may have been missed
  isReconnect: boolean;
}

export interface LiveUpdateConnectionOptions {
  url: string;
  // fired on every open, first and re-, so the owner can catch up on what it missed
  onConnect: (info: LiveUpdateConnectInfo) => void;
  // parsed messages, heartbeats already consumed
  onMessage: (message: SocketMessage) => void;
  createSocket?: (url: string) => LiveUpdateSocket | undefined;
  isVisible?: () => boolean;
  isOnline?: () => boolean;
  random?: () => number;
}

// idle: not started, or stopped. connecting: socket created, not yet open. open: connected.
// waiting: a reconnect timer is pending. suspended: disconnected and deliberately not retrying,
// because the tab is hidden, the browser is offline, the page is being hidden or unloaded, or the
// tab was hidden long enough to give up.
export type LiveUpdateState = 'idle' | 'connecting' | 'open' | 'waiting' | 'suspended';

// Chrome throws synchronously when a secure page opens an insecure socket, and a dev server reached
// through an HTTPS tunnel can hit exactly that, so a failed constructor must never abort startup
function createBrowserSocket(url: string): LiveUpdateSocket | undefined {
  try {
    return new WebSocket(url);
  } catch (error) {
    console.log(`live updates unavailable at ${url}: ${error}`);
    return undefined;
  }
}

export class LiveUpdateConnection {
  private readonly options: LiveUpdateConnectionOptions;
  private readonly createSocket: (url: string) => LiveUpdateSocket | undefined;
  private readonly isVisible: () => boolean;
  private readonly isOnline: () => boolean;
  private readonly random: () => number;

  private currentState: LiveUpdateState = 'idle';
  private socket: LiveUpdateSocket | undefined;
  private attempt = 0;
  private hasOpened = false;
  // a successful fetch earns one early retry per failure streak, not one per request: an API that
  // answers while the upgrade is refused would otherwise reconnect on every poll instead of backing off
  private fetchRetryUsed = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private connectTimer: ReturnType<typeof setTimeout> | undefined;
  private watchdogTimer: ReturnType<typeof setTimeout> | undefined;
  private hiddenCloseTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(options: LiveUpdateConnectionOptions) {
    this.options = options;
    this.createSocket = options.createSocket ?? createBrowserSocket;
    this.isVisible = options.isVisible ?? (() => document.visibilityState === 'visible');
    this.isOnline = options.isOnline ?? (() => navigator.onLine);
    this.random = options.random ?? Math.random;
  }

  get state(): LiveUpdateState {
    return this.currentState;
  }

  start(): void {
    if (this.currentState !== 'idle') {
      return;
    }
    this.attempt = 0;
    this.hasOpened = false;
    this.connectWhenAllowed();
  }

  stop(): void {
    this.clearTimers();
    clearTimeout(this.hiddenCloseTimer);
    this.hiddenCloseTimer = undefined;
    this.releaseSocket(1000, 'stopping');
    this.attempt = 0;
    this.currentState = 'idle';
  }

  // The tab came back. Cancels the give-up timer and reconnects now, because a user who just looked
  // at the app should not wait out a backoff delay to see fresh content.
  noteVisible(): void {
    clearTimeout(this.hiddenCloseTimer);
    this.hiddenCloseTimer = undefined;
    this.connectImmediately();
  }

  noteHidden(): void {
    clearTimeout(this.hiddenCloseTimer);
    this.hiddenCloseTimer = setTimeout(() => {
      this.hiddenCloseTimer = undefined;
      if (this.currentState === 'idle' || this.isVisible()) {
        return;
      }
      this.suspend(1000, 'tab hidden');
    }, liveUpdateHiddenCloseMs);
  }

  // The page is being hidden or unloaded. An open socket would keep the document out of the
  // back-forward cache in some browsers, so give it up now; pageshow brings it straight back.
  notePageHide(): void {
    if (this.currentState === 'idle') {
      return;
    }
    this.suspend(1000, 'page hidden');
  }

  noteOnline(): void {
    this.connectImmediately();
  }

  // Cancels a pending retry only. An open socket is deliberately left alone: the browser tears it
  // down on its own, and the watchdog and connect timeout cover the cases where it does not.
  noteOffline(): void {
    this.clearReconnectTimer();
    if (this.currentState === 'waiting') {
      this.currentState = 'suspended';
    }
  }

  // Any successful HTTP request proves the network is back, which is often the first hard evidence a
  // mobile browser gives us - worth one early retry. Only one, and without resetting the backoff: if
  // HTTP works but the socket upgrade keeps failing, the next retries must keep spreading out.
  noteFetchSucceeded(): void {
    // suspended too: a webview that foregrounds without firing visibilitychange or pageshow (older
    // iOS app builds do, see page_foreground.ts) still makes requests, and those are its only
    // way back; connectWhenAllowed() re-checks visibility and the network either way
    if ((this.currentState !== 'waiting' && this.currentState !== 'suspended') || this.fetchRetryUsed) {
      return;
    }
    this.fetchRetryUsed = true;
    this.clearReconnectTimer();
    this.connectWhenAllowed();
  }

  private connectImmediately(): void {
    if (this.currentState === 'idle' || this.currentState === 'open' || this.currentState === 'connecting') {
      return;
    }
    this.clearReconnectTimer();
    this.attempt = 0;
    this.connectWhenAllowed();
  }

  private connectWhenAllowed(): void {
    if (!this.isVisible() || !this.isOnline()) {
      this.currentState = 'suspended';
      return;
    }
    this.currentState = 'connecting';
    const socket = this.createSocket(this.options.url);
    if (!socket) {
      this.socket = undefined;
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;
    // bound to this socket instance, so a stale socket's late events are ignored below rather than
    // scheduling a second reconnect alongside the live one
    socket.onopen = () => this.handleOpen(socket);
    socket.onmessage = (event) => this.handleMessage(socket, event);
    socket.onclose = () => this.handleDisconnect(socket);
    socket.onerror = () => this.handleDisconnect(socket);
    this.connectTimer = setTimeout(() => {
      this.connectTimer = undefined;
      if (socket !== this.socket) {
        return;
      }
      this.detach(socket);
      this.socket = undefined;
      socket.close(1000, 'connect timeout');
      this.scheduleReconnect();
    }, liveUpdateConnectTimeoutMs);
  }

  private handleOpen(socket: LiveUpdateSocket): void {
    if (socket !== this.socket) {
      return;
    }
    this.clearConnectTimer();
    this.currentState = 'open';
    this.resetWatchdog(socket);
    if (getEnvironment() === Environment.Dev) {
      console.log('live updates connected');
    }
    const isReconnect = this.hasOpened;
    this.hasOpened = true;
    this.options.onConnect({ isReconnect });
  }

  private handleMessage(socket: LiveUpdateSocket, event: MessageEvent): void {
    if (socket !== this.socket) {
      return;
    }
    // any message is proof of life, heartbeats included - and proof the connection is healthy, which
    // is what earns a fresh backoff. An open that dies before its first message keeps the old one, so
    // a flapping server is retried less and less often, not more
    this.attempt = 0;
    this.fetchRetryUsed = false;
    this.resetWatchdog(socket);
    let message: unknown;
    try {
      message = JSON.parse(String(event.data));
    } catch {
      console.log(`ignoring malformed live-update message: ${String(event.data)}`);
      return;
    }
    if (!message || typeof message !== 'object') {
      console.log(`ignoring malformed live-update message: ${String(event.data)}`);
      return;
    }
    if (isHeartbeatMessage(message)) {
      return;
    }
    // what an unrecognized type means is the owner's business, not this transport's
    this.options.onMessage(message as SocketMessage);
  }

  // A close or an error of the current socket means the same thing: reconnect. Every deploy and
  // every local server restart produces a clean 1001, and it gets the base delay like anything else
  // - the server is usually back within seconds, and full jitter spreads the herd.
  private handleDisconnect(socket: LiveUpdateSocket): void {
    if (socket !== this.socket) {
      return;
    }
    this.clearConnectTimer();
    this.clearWatchdog();
    this.detach(socket);
    this.socket = undefined;
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.currentState === 'idle') {
      return;
    }
    this.clearReconnectTimer();
    // no point arming a timer that would only find the tab hidden or the browser offline when it
    // fires: a visibility or online trigger will connect us the moment there is a reason to
    if (!this.isVisible() || !this.isOnline()) {
      this.currentState = 'suspended';
      return;
    }
    const backoff = Math.min(liveUpdateBackoffMaxMs, liveUpdateBackoffInitialMs * 2 ** this.attempt);
    // full jitter: a uniform delay in [0, backoff], so a restarted server's clients do not return in
    // a single wave
    const delay = backoff * this.random();
    if (backoff < liveUpdateBackoffMaxMs) {
      this.attempt += 1;
    }
    this.currentState = 'waiting';
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connectWhenAllowed();
    }, delay);
  }

  // Silence past the watchdog window means the socket is dead whether or not the browser noticed, so
  // give up on it and reconnect rather than waiting for an onclose that may never come.
  private resetWatchdog(socket: LiveUpdateSocket): void {
    this.clearWatchdog();
    this.watchdogTimer = setTimeout(() => {
      this.watchdogTimer = undefined;
      if (socket !== this.socket) {
        return;
      }
      this.detach(socket);
      this.socket = undefined;
      socket.close(1000, 'no messages');
      this.scheduleReconnect();
    }, liveUpdateWatchdogMs);
  }

  // disconnect on purpose and stop retrying until a trigger says otherwise
  private suspend(code: number, reason: string): void {
    this.clearTimers();
    this.releaseSocket(code, reason);
    this.currentState = 'suspended';
  }

  private releaseSocket(code: number, reason: string): void {
    const socket = this.socket;
    this.socket = undefined;
    if (!socket) {
      return;
    }
    this.detach(socket);
    socket.close(code, reason);
  }

  private detach(socket: LiveUpdateSocket): void {
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
  }

  private clearTimers(): void {
    this.clearReconnectTimer();
    this.clearConnectTimer();
    this.clearWatchdog();
  }

  private clearReconnectTimer(): void {
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
  }

  private clearConnectTimer(): void {
    clearTimeout(this.connectTimer);
    this.connectTimer = undefined;
  }

  private clearWatchdog(): void {
    clearTimeout(this.watchdogTimer);
    this.watchdogTimer = undefined;
  }
}

// The socket always lives on the page origin, so one line covers plain localhost, a dev server
// behind an HTTPS tunnel, test and production alike.
export function getLiveUpdateSocketUrl(location: { protocol: string; host: string }): string {
  const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${protocol}://${location.host}${liveUpdatesSocketPath}`;
}

// The app's single connection. Everything above is DOM-free so the state machine can be tested
// under fake timers; this is where the browser events that drive it are actually subscribed.
let appConnection: LiveUpdateConnection | undefined;

export function startAppLiveUpdates(
  handlers: { onConnect: (info: LiveUpdateConnectInfo) => void; onMessage: (message: SocketMessage) => void },
  // a test seam only: a fake socket factory (and friends) in place of the real browser WebSocket
  overrides?: Partial<LiveUpdateConnectionOptions>,
): LiveUpdateConnection {
  if (appConnection) {
    return appConnection;
  }
  const connection = new LiveUpdateConnection({
    url: getLiveUpdateSocketUrl(window.location),
    onConnect: handlers.onConnect,
    onMessage: handlers.onMessage,
    ...overrides,
  });
  appConnection = connection;
  // a return to the foreground reconnects at once, a departure starts the give-up timer;
  // page_foreground.ts decides which browser and store-app events count as either
  subscribePageForeground((foreground) => (foreground ? connection.noteVisible() : connection.noteHidden()));
  // pagehide is pageshow's mirror: giving the socket up there is what keeps the page eligible for
  // the back-forward cache in browsers that refuse to cache a document with an open WebSocket.
  window.addEventListener('pagehide', () => connection.notePageHide());
  window.addEventListener('online', () => connection.noteOnline());
  window.addEventListener('offline', () => connection.noteOffline());
  connection.start();
  return connection;
}

// Tells the app's connection that an HTTP request just succeeded, which is often the first hard
// evidence a mobile browser gives that the network is back. A no-op while connected, and a no-op
// before startAppLiveUpdates() has run, so it is safe on the fetch hot path.
export function noteAppFetchSucceeded(): void {
  appConnection?.noteFetchSucceeded();
}
